/**
 * Settings > Zones (SAM-946, mock Zones v81 approved 15 Oct 2026): the tenant's own catalog of zones (Zona Norte,
 * Marina…). An internal gate points to one zone and each estate group picks its zone. Only the tenant admin and
 * Xhinola create, rename or delete them; a zone in use cannot be deleted (crud answers 409). Pure helpers.
 */
import type { Gate, Zone } from '../../../api/generated';

/**
 * The catalog needs `zone.tenant_id` and the crud rules of the zones migration (sam-lambda-functions, SAM-946). Until
 * that is in an environment the screens, the menu item and every `zone_id` the admin would send stay off.
 */
export const ZONES_ENABLED = false;

/**
 * Whether a screen may read the catalog: only the tenant admin and Xhinola (the roles that can use it). Any other role
 * would get a 403 toast, or a 500 that opens a «[500] crud» ticket.
 */
export const readsZones = (enabled: boolean, isAdmin: boolean): boolean => enabled && isAdmin;

export interface ZoneRow {
  id: number;
  name: string;
  /** Internal gates that point to this zone. */
  gates: number;
  /** Estate groups that picked this zone. */
  groups: number;
}

type WithZone = { zone_id?: number | null };

const countBy = (items: WithZone[]): Map<number, number> => {
  const m = new Map<number, number>();
  for (const it of items) if (typeof it.zone_id === 'number') m.set(it.zone_id, (m.get(it.zone_id) ?? 0) + 1);
  return m;
};

export const toZoneRows = (zones: Zone[], gates: Gate[], groups: WithZone[]): ZoneRow[] => {
  const g = countBy(gates as WithZone[]);
  const eg = countBy(groups);
  return zones
    .filter((z): z is Zone & { id: number } => typeof z.id === 'number')
    .map((z) => ({ id: z.id, name: z.name ?? '', gates: g.get(z.id) ?? 0, groups: eg.get(z.id) ?? 0 }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }));
};

export const filterZones = (rows: ZoneRow[], search: string): ZoneRow[] => {
  const q = search.trim().toLowerCase();
  return q ? rows.filter((r) => r.name.toLowerCase().includes(q)) : rows;
};

export const gatesText = (n: number): string => (n === 0 ? 'No gates yet' : `${n} internal gate${n === 1 ? '' : 's'}`);

/** A zone can be deleted only when no gate and no estate group uses it (the server re-checks: 409). */
export const canDeleteZone = (row: Pick<ZoneRow, 'gates' | 'groups'>): boolean => row.gates === 0 && row.groups === 0;

/** Why Delete is off, naming what has to move first (Z4 / Z4c). */
export const deleteBlockedText = (row: Pick<ZoneRow, 'gates' | 'groups'>): string => {
  if (canDeleteZone(row)) return '';
  if (row.gates > 0 && row.groups > 0) return 'To delete this zone, move its estate groups and gates to another zone.';
  return row.groups > 0
    ? 'To delete this zone, move its estate groups to another zone.'
    : `To delete this zone, first move its internal gate${row.gates === 1 ? '' : 's'} to another zone.`;
};

/** The name is required and unique in the tenant, whatever its case or surrounding spaces. */
export const zoneNameIssue = (name: string, rows: ZoneRow[], editingId?: number): string | undefined => {
  const n = name.trim();
  if (!n) return 'Enter a name.';
  if (rows.some((r) => r.id !== editingId && r.name.trim().toLowerCase() === n.toLowerCase())) return 'A zone with this name already exists.';
  return undefined;
};

/**
 * What a refused write means. Saving: 409 = the name is taken. Deleting: 409 = the zone has gates or estate groups
 * (someone linked one after this page loaded).
 */
export const zoneFailure = (err: unknown, action: 'create' | 'save' | 'delete'): { field?: 'name'; message: string } => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 409 && action === 'delete') return { message: 'This zone has gates or estate groups. Move them to another zone first.' };
  if (status === 409) return { field: 'name', message: 'A zone with this name already exists.' };
  if (status === 403) return { message: 'Only the tenant admin can change zones.' };
  return { message: action === 'delete' ? 'Could not delete the zone. Try again.' : action === 'save' ? 'Could not save the zone. Try again.' : 'Could not create the zone. Try again.' };
};
